import { ComponentProps, ReactElement, useEffect, useId, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog } from "@industriousoffice/react-components";

// Associate the shared dialog's visible content with its existing Radix IDs.
// The shared component currently renders plain divs instead of Title/Description.
function associateContent(
  node: HTMLElement | null,
  attribute: string,
  fallbackId: string
): void {
  const dialog = node?.closest('[role="alertdialog"]');
  if (node == null || dialog == null) return;
  const id = dialog.getAttribute(attribute) || fallbackId;
  node.setAttribute("id", id);
  dialog.setAttribute(attribute, id);
}

export function PaymentConfirmationDialog({
  title,
  description,
  onClose,
  isLoading,
  onOpenChange,
  isOpen = false,
  cancelButtonClassName = "",
  confirmButtonClassName = "",
  buttonRowClassName = "",
  contentClassName = "",
  titleClassName = "",
  descriptionClassName = "",
  leftContentClassName = "",
  ...props
}: ComponentProps<typeof ConfirmationDialog>): ReactElement {
  const id = useId();
  const { t } = useTranslation("paymentMethods");
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    // The shared dialog's Cancel ref does not reach its button. Focus it once
    // the portal mounts so Radix can keep keyboard focus inside the alert.
    const frame = requestAnimationFrame(() => {
      const dialog = titleRef.current?.closest<HTMLElement>(
        '[role="alertdialog"]'
      );
      const target =
        dialog?.querySelector<HTMLElement>(
          ".payment-confirmation-cancel:not(:disabled)"
        ) ?? dialog?.querySelector<HTMLElement>("button:not(:disabled)");
      (target ?? dialog)?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected)
        previousFocus.focus();
    };
  }, [id, isOpen]);
  return (
    <ConfirmationDialog
      {...props}
      isOpen={isOpen}
      open={isOpen}
      cancelButtonClassName={`payment-confirmation-cancel ${cancelButtonClassName}`}
      confirmButtonClassName={`payment-confirmation-confirm ${confirmButtonClassName}`}
      buttonRowClassName={`payment-confirmation-buttons ${buttonRowClassName}`}
      contentClassName={`payment-confirmation ${contentClassName}`}
      titleClassName={`payment-confirmation-title ${titleClassName}`}
      descriptionClassName={`payment-confirmation-description ${descriptionClassName}`}
      leftContentClassName={`payment-confirmation-content ${leftContentClassName}`}
      rightContent={
        <button
          type="button"
          className="payment-confirmation-close"
          aria-label={t("Close confirmation")}
          disabled={isLoading}
          onClick={onClose}
        >
          <XMarkIcon width={24} height={24} aria-hidden="true" />
        </button>
      }
      isLoading={isLoading}
      onClose={onClose}
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
        onOpenChange?.(open);
      }}
      title={
        title == null ? undefined : (
          <h2
            ref={(node) => {
              titleRef.current = node;
              associateContent(node, "aria-labelledby", `${id}-title`);
            }}
          >
            {title}
          </h2>
        )
      }
      description={
        description == null ? undefined : (
          <div
            ref={(node) =>
              associateContent(node, "aria-describedby", `${id}-description`)
            }
          >
            {description}
          </div>
        )
      }
    />
  );
}
